import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList,
  moveItemInArray
} from '@angular/cdk/drag-drop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  afterRenderEffect,
  computed,
  input,
  signal,
  viewChildren
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { ICONS } from '@gfe/core/services/icons/icons.service';
import { isNil } from '@gfe/core/utils/shared-utils';
import { TooltipComponent } from '@gfe/shared/components/tooltip/tooltip.component';

export interface CardConfig {
  id: string;
  title: string;
  description?: string;
  tooltip?: string;
  iconClass: string;
  isPinned: boolean;
  route?: string;
  labels: Record<string, string>;
  hasAccent?: boolean;
}

export interface CardsListComponentConfig {
  noDataMessage: string;
  onClick?: (card: CardConfig) => void;
  /**
   * When provided, each card renders a pin toggle, and pinned cards are also
   * shown as compact shortcut tiles above the grid. A pinned card keeps its
   * place in the grid.
   */
  onPinToggle?: (card: CardConfig) => void;
  /** Accessible label / tooltip for the pin action on an unpinned card. */
  pinTooltip?: string;
  /** Accessible label / tooltip for the unpin action on a pinned card. */
  unpinTooltip?: string;
  /**
   * When provided, cards become draggable and reordering is enabled. The
   * callback receives the full list of card ids in their new display order
   * after a drop. Callers persist this order and feed it back through `cards`.
   */
  onReorder?: (orderedIds: string[]) => void;
  /** Accessible label of the pinned shortcuts row. */
  pinnedLabel?: string;
  /** Ids in the order the pinned shortcuts are shown; unlisted pinned cards follow. */
  pinnedOrder?: string[];
  /**
   * When provided, the pinned shortcuts become draggable. The callback receives
   * the ids of the shown shortcuts in their new order; the grid is untouched.
   */
  onPinnedReorder?: (orderedIds: string[]) => void;
  cards: CardConfig[];
}

@Component({
  selector: 'app-cards-list',
  imports: [
    RouterLink,
    NgTemplateOutlet,
    NgClass,
    CdkDropList,
    CdkDrag,
    CdkDragPreview,
    CdkDragPlaceholder,
    TooltipComponent
  ],
  templateUrl: './cards-list.component.html',
  styles: `
    /* The card lifted under the cursor: elevated and clearly "picked up".
       (CDK owns the preview's transform to track the pointer, so we only
       style elevation here — a CSS transform would be overridden.) */
    .cdk-drag-preview {
      border-radius: 0.5rem;
      box-shadow:
        0 12px 24px -6px rgb(0 0 0 / 35%),
        0 6px 10px -4px rgb(0 0 0 / 25%);
    }

    /* NOTE: no opacity:0 here — we render a custom *cdkDragPlaceholder and want
       it visible so the user sees exactly where the card will land. */

    /* Snap the picked-up card back into the grid without the tilt lingering. */
    .cdk-drag-animating {
      transition: transform 220ms cubic-bezier(0, 0, 0.2, 1);
    }

    /* Smoothly animate the surrounding cards as they shuffle to open the slot. */
    .cdk-drop-list-dragging .group:not(.cdk-drag-placeholder) {
      transition: transform 220ms cubic-bezier(0, 0, 0.2, 1);
    }

    /* Gentle attention pulse on the landing slot. */
    @keyframes cards-list-slot-pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.55;
      }
    }
  `
})
export class CardsListComponent {
  protected readonly ICONS = ICONS;
  config = input.required<CardsListComponentConfig>();
  selected = input<string | undefined>();

  protected pinningEnabled = computed(() => !isNil(this.config().onPinToggle));
  protected reorderEnabled = computed(() => !isNil(this.config().onReorder));
  protected pinnedReorderEnabled = computed(() => !isNil(this.config().onPinnedReorder));

  protected displayCards = computed<CardConfig[]>(() => {
    // Dedupe by id (keep first): the template tracks by `card.id`, so duplicate
    // ids would crash rendering with NG0955. Duplicates can occur when two
    // entities share a name; rendering the same card twice is never intended.
    const seenIds = new Set<string>();
    return this.config().cards.filter((card) => {
      if (seenIds.has(card.id)) return false;
      seenIds.add(card.id);
      return true;
    });
  });

  /** Compact shortcuts to the pinned cards, shown above the grid. */
  protected pinnedShortcuts = computed<CardConfig[]>(() => {
    if (!this.pinningEnabled()) return [];
    const order = this.config().pinnedOrder ?? [];
    const rank = (card: CardConfig): number => {
      const index = order.indexOf(card.id);
      return index === -1 ? Infinity : index;
    };
    return this.displayCards()
      .filter((card) => card.isPinned)
      .sort((a, b) => rank(a) - rank(b));
  });

  /** Ids of the pinned shortcuts whose title overflows its clamp, so only they get a tooltip. */
  protected truncatedShortcutIds = signal<ReadonlySet<string>>(new Set());
  private shortcutTitles = viewChildren<ElementRef<HTMLElement>>('shortcutTitle');

  constructor() {
    // Overflow is only measurable once the titles are laid out, hence after render.
    afterRenderEffect(() => {
      const truncated = new Set(
        this.shortcutTitles()
          .map((ref) => ref.nativeElement)
          .filter((element) => element.scrollHeight > element.clientHeight)
          .map((element) => element.dataset['cardId'] ?? '')
      );
      const current = this.truncatedShortcutIds();
      if (
        truncated.size !== current.size ||
        [...truncated].some((id) => !current.has(id))
      ) {
        this.truncatedShortcutIds.set(truncated);
      }
    });
  }

  /** The first stat of a card (its total), shown on its pinned shortcut. */
  protected primaryLabelValue(card: CardConfig): string | undefined {
    return Object.values(card.labels)[0];
  }

  /** Reorders the cards after a drag-and-drop and emits the new id order. */
  protected drop(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex === event.currentIndex) return;
    const ids = this.displayCards().map((card) => card.id);
    moveItemInArray(ids, event.previousIndex, event.currentIndex);
    this.config().onReorder?.(ids);
  }

  protected dropPinned(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex === event.currentIndex) return;
    const ids = this.pinnedShortcuts().map((card) => card.id);
    moveItemInArray(ids, event.previousIndex, event.currentIndex);
    this.config().onPinnedReorder?.(ids);
  }

  handleCardClick(card: CardConfig): void {
    // Only handle onClick scenarios
    // Route navigation is now handled natively by <a [routerLink]>
    const onClick = this.config().onClick;
    if (!isNil(onClick)) {
      onClick(card);
    }
  }

  /**
   * Toggles a card's pinned state without triggering the card's own
   * navigation/click, since the pin overlays the routed anchor.
   */
  protected togglePin(event: Event, card: CardConfig): void {
    event.preventDefault();
    event.stopPropagation();
    this.config().onPinToggle?.(card);
  }

  protected labelsEntries(object: Record<string, string>): [string, string][] {
    return Object.entries(object);
  }
}
